// src/components/Sidebar/Sidebar.tsx
import * as React from "react";
import {
  Box,
  CssBaseline,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Popover,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

export interface SubItem {
  name: string;
  link: string;
  icon: React.ReactNode;
}

export interface PageItem {
  name: string;
  link?: string;
  icon: React.ReactNode;
  submenu?: SubItem[];
}

interface SidebarProps {
  pages: PageItem[];
  drawerWidth?: number;
  primaryColorKey: string; // e.g. 'campaignsPrimary', 'communityPrimary', etc.
}

/**
 * Generic sidebar that supports:
 *  - Simple links
 *  - Hover-based submenus (popovers)
 *  - Dynamic colors from theme
 *
 * Usage:
 * <Sidebar pages={pages} primaryColorKey="campaignsPrimary" />
 */
export default function Sidebar({
  pages,
  drawerWidth = 50,
  primaryColorKey,
}: SidebarProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [activeSubmenu, setActiveSubmenu] = React.useState<string | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, pageName: string) => {
    setAnchorEl(event.currentTarget);
    setActiveSubmenu(pageName);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>, pageName: string) => {
    setTimeout(() => {
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      if (!relatedTarget || !relatedTarget.closest(`#popover-${pageName}`)) {
        setAnchorEl(null);
        setActiveSubmenu(null);
      }
    }, 50);
  };

  const handlePopoverLeave = (event: React.MouseEvent<HTMLElement>, pageName: string) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !relatedTarget.closest(`[data-page="${pageName}"]`)) {
      setAnchorEl(null);
      setActiveSubmenu(null);
    }
  };
  
  const primaryColor =
    (theme.custom as Record<string, any>)[primaryColorKey]?.main.dark ||
    theme.custom.base.primary.dark;


  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Box
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          bgcolor: theme.custom.ui.sidebars.left.dark,
          color: theme.custom.base.text.primary.dark,
          height: "100vh",
          position: "relative",
          boxShadow: 2,
          borderRight: "none",
        }}
      >
        <List>
          {pages.map((page) => (
            <ListItem
              key={page.name}
              disablePadding
              sx={{ display: "block" }}
              data-page={page.name}
              onMouseEnter={(e) => page.submenu && handleMouseEnter(e, page.name)}
              onMouseLeave={(e) => page.submenu && handleMouseLeave(e, page.name)}
            >
              <Tooltip title={page.name} placement="right">
                <ListItemButton
                  component={page.link ? "a" : "div"}
                  href={page.link}
                  sx={{
                    justifyContent: "center",
                    py: 1,
                    "& .MuiListItemIcon-root": {
                      color:
                        activeSubmenu === page.name
                          ? primaryColor
                          : theme.custom.base.text.primary.dark,
                      transition: "color 0.2s ease",
                    },
                    "&:hover .MuiListItemIcon-root": {
                      color: primaryColor,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: "center",
                    }}
                  >
                    {page.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>

              {/* Submenu */}
              {page.submenu && (
                <Popover
                  id={`popover-${page.name}`}
                  open={activeSubmenu === page.name}
                  anchorEl={anchorEl}
                  onClose={() => setActiveSubmenu(null)}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        bgcolor: theme.custom.ui.sidebars.left.dark,
                        mt: 0.5,
                        boxShadow: 3,
                        borderRadius: 2,
                        width: drawerWidth,
                        pointerEvents: "auto",
                      },
                      onMouseEnter: () => setActiveSubmenu(page.name),
                      onMouseLeave: (e: React.MouseEvent<HTMLElement>) =>
                        handlePopoverLeave(e, page.name),
                    },
                  }}
                  disableRestoreFocus
                >
                  <List sx={{ py: 0.5 }}>
                    {page.submenu.map((sub) => (
                      <ListItem key={sub.name} disablePadding>
                        <Tooltip title={sub.name} placement="right">
                          <ListItemButton
                            component="a"
                            href={sub.link}
                            sx={{
                              gap: 1,
                              "&:hover .MuiListItemIcon-root": {
                                color: primaryColor,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 32,
                                color: theme.custom.base.text.primary.dark,
                              }}
                            >
                              {sub.icon}
                            </ListItemIcon>
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                </Popover>
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
}
